import { Editor, Element, Range, Text } from '../../core';
import type { Path } from '../../core';
import type { CreateDecorate, EditorBag } from '../../types';
import { PARAGRAPH_PLUGIN_ID } from '../paragraph/types';
import { matchHeadingLikePrefix } from './matcher';
import type { HeadingDecorationPluginPropertyOptions } from './types';

/**
 * RP-6005 (M2) — decorate the `Label:` run of a heading-like paragraph so it
 * renders as a heading, without persisting anything. Heading-ness is derived
 * via the RP-6004 matcher (keyed off the recognised section-label set). Mirrors
 * the existing `highlighted` plugin.
 *
 * Guarded to the FIRST text leaf of a TOP-LEVEL, non-inline paragraph — so a
 * `Label:` typed inside a list item or after an inline element isn't styled.
 */
const isTopLevelParagraph = (editor: Editor, path: Path): boolean => {
  // A leaf of a top-level block has path `[blockIndex, leafIndex]`. Read the parent block directly
  // from `editor.children` rather than via `Editor.parent`, which throws on invalid paths (forcing a
  // try/catch) and adds avoidable overhead on this hot path (decorate runs per text node). Nested
  // blocks such as list items have deeper paths and are excluded by the length check.
  if (path.length !== 2) return false;
  const parent = editor.children[path[0]];
  return (
    Element.isElement(parent) &&
    parent.type === PARAGRAPH_PLUGIN_ID &&
    (parent as { shouldForceInline?: boolean }).shouldForceInline !== true
  );
};

export const getDecorate: CreateDecorate<HeadingDecorationPluginPropertyOptions> = ({
  headingLabels,
}) => {
  return ([node, path], editorBag: EditorBag) => {
    const ranges: Array<Range> = [];

    if (!Text.isText(node) || typeof node.text !== 'string') return ranges;
    if (path[path.length - 1] !== 0) return ranges;
    if (!isTopLevelParagraph(editorBag.editor, path)) return ranges;

    const match = matchHeadingLikePrefix(node.text, headingLabels);
    if (match == null) return ranges;

    ranges.push({
      anchor: { path, offset: match.start },
      focus: { path, offset: match.end },
      headingDecoration: true,
    });

    return ranges;
  };
};
